import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { bg } from "date-fns/locale";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import { IEventHistoryItemViewModel } from "../../../../../common/models/eventHistoryItemViewModel";
import { HistoryItemKind } from "../../../../../common/enums";
import {
  BULK_DELETE_CONFIRMATION_WORD,
  bulkDeleteUserEventsFormDefaultValues,
  bulkDeleteUserEventsFormSchema,
  IBulkDeleteUserEventsFormModel,
} from "../models/bulkDeleteUserEventsFormModel";

interface IUseBulkDeleteUserEventsDialogProps {
  userId?: number;
  visible: boolean;
  history?: IEventHistoryItemViewModel[];
  onHide: () => void;
}

interface IEventOption {
  id: number;
  name: string;
  disabled: boolean;
}

export const useBulkDeleteUserEventsDialog = ({
  userId,
  visible,
  history,
  onHide,
}: IUseBulkDeleteUserEventsDialogProps) => {
  const form = useForm<IBulkDeleteUserEventsFormModel>({
    defaultValues: bulkDeleteUserEventsFormDefaultValues,
    resolver: yupResolver(bulkDeleteUserEventsFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!visible) {
      form.reset(bulkDeleteUserEventsFormDefaultValues);
    }
  }, [visible, form]);

  const eventOptions: IEventOption[] = useMemo(() => {
    if (!history) {
      return [];
    }

    return history
      .filter(
        (item) =>
          item.kind !== HistoryItemKind.GymVisit && item.userEventId != null,
      )
      .slice()
      .sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      )
      .map((item) => ({
        id: item.userEventId as number,
        name: formatEventLabel(item),
        disabled: item.paymentType != null,
      }));
  }, [history]);

  const userEventIds = form.watch("userEventIds") ?? [];
  const confirmationWord = form.watch("confirmationWord") ?? "";

  const isConfirmationWordValid =
    confirmationWord.trim().toLowerCase() === BULK_DELETE_CONFIRMATION_WORD;

  const canDelete = userEventIds.length > 0 && isConfirmationWordValid;

  const { mutate, isLoading } = useApiMutation();

  const handleDelete = () => {
    if (!canDelete || !userId) {
      return;
    }

    const count = userEventIds.length;
    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да изтриете перманентно избраните ${count} резервации? Тази операция е абсолютно необратима — данните няма да могат да бъдат възстановени.`,
      () => {
        mutate(
          {
            endpoint: endpointsMetadata.planner.bulkPermanentlyDeleteUserEvents,
            method: "post",
            globalIsLoading: true,
            payload: {
              userId,
              userEventIds,
            },
            invalidateKeys: [[endpointsMetadata.user.getUser, String(userId)]],
          },
          {
            onSuccess: () => {
              onHide();
            },
          },
        );
      },
      () => {},
      "warning",
      "Да, изтрий",
      "Не",
    );
  };

  return {
    form,
    eventOptions,
    canDelete,
    isLoading,
    handleDelete,
  };
};

const formatEventLabel = (item: IEventHistoryItemViewModel): string => {
  const formattedDate = dateUtility.formatDate(
    item.date,
    "dd/MM/yyyy, HH:mm",
    bg,
  );
  return `${item.eventName} — ${formattedDate}`;
};
